<template>
    <component
        :is="props.game ? BaseLink : 'div'"
        v-bind="linkData"
        ref="component"
        :class="$style.container"
        data-interface-target="game"
    >
        <CardContainer
            :appearance="cardAppearance"
            :class="[
                $style.game_card_override,
                props.classForGame,
                {
                    [$style.square_override]: isSquareTypeCard,
                    [$style.card_with_scale_override]: props.game && props.withScaleOnHover,
                    // Can't use :hover because after opening the game in a new tab, the card stays enlarged when returning.
                    [$style.hovered_override]: isMouseOver,
                },
            ]"
            :style="{ transformOrigin }"
            :iconImageClass="props.iconImageClass"
            :imageClass="props.imageClass"
            :alt="props.game?.title"
            :src="cardSrc"
            :responsive="props.responsive"
            :lazy="props.lazy"
            :preload="props.preload"
            :approximateWidth="props.approximateWidth"
            :placeholder="!props.game"
            v-bind="microdataAttrs"
            :itemprop="props.disableMicrodata ? '' : 'image'"
            :iconSrc="props.iconSrc"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave"
        >
            <GameVideo
                ref="videoElemRef"
                :videoId="props.game?.videoId"
                :videoUrl="props.game?.videoUrl"
            />
            <div
                v-if="isUnavailableOnPlatform"
                :class="$style.platform_overlay"
            >
                <Typography
                    size="s"
                    type="label"
                    accent
                    :responsive="false"
                    :class="$style.platform_overlay_label"
                >
                    {{ availabilityText }}
                </Typography>
            </div>
            <Typography
                v-else-if="isOldTypeCard && props.game"
                size="xs"
                type="label"
                accent
                :responsive="false"
                :itemprop="props.disableMicrodata ? undefined : 'name'"
                :class="$style.title"
            >
                {{ props.game.title }}
            </Typography>
            <div
                v-if="props.game?.debugInfo && userStore.isCompanyEmployeeLoggedIn"
                :class="$style.debug_icon"
                @click.prevent.stop="showDebugInfo"
            >
                ⓘ
            </div>
        </CardContainer>
        <Badge
            v-if="props.withBadges"
            :badges="props.game?.badges"
            :isHide="isMouseOver"
            :className="$style.badge_override"
        />
        <Typography
            v-if="props.type === 'square' || props.type === 'rectangle'"
            size="xs"
            type="label"
            accent
            :responsive="false"
            :itemprop="props.disableMicrodata ? undefined : 'name'"
            :class="$style.new_title"
        >
            {{ gameTitle }}
        </Typography>
    </component>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { ComponentPublicInstance, computed, onMounted, ref, watch } from 'vue'
import { type RouteLocationRaw } from 'vue-router'

import Badge from '@/components/Badge/Badge.vue'
import BaseLink from '@/components/BaseLink.vue'
import CardContainer, { CardAppearance } from '@/components/CardContainer.vue'
import GameVideo from '@/components/GameVideo.vue'
import Typography from '@/components/Typography.vue'
import { useGetExternalUrl } from '@/composables/url-helpers'
import type { CtrContext } from '@/composables/use-ctr-context'
import { isHeroVideoAutoplayEnabled } from '@/composables/use-hero-video-autoplay'
import { useTr } from '@/composables/use-tr'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useDebugDialogStore } from '@/store/debug-dialog-store'
import { useLayoutStore } from '@/store/layout-store'
import { useCtrStore } from '@/store/logger/ctr-store'
import { useUserStore } from '@/store/user-store/user-store'
import type { LinkTarget, ShortGameType } from '@/types'

type VueClassBinding = string | Record<string, unknown> | (string | Record<string, unknown>)[]

interface GameCardProps {
    game?: ShortGameType
    appearance?: CardAppearance
    responsive?: boolean
    targetLink?: LinkTarget
    approximateWidth?: number
    lazy?: boolean
    preload?: boolean
    classForGame?: VueClassBinding
    iconSrc?: string
    iconImageClass?: VueClassBinding
    imageClass?: VueClassBinding
    withBadges?: boolean
    withScaleOnHover?: boolean
    ctrContext?: CtrContext
    type?: 'square' | 'square-without-title' | 'rectangle'
    to?: RouteLocationRaw
    disableMicrodata?: boolean
    autoplayVideoInViewport?: boolean
}

const props = withDefaults(defineProps<GameCardProps>(), {
    lazy: true,
})

const layoutStore = useLayoutStore()

const appStore = useAppStore()
const userStore = useUserStore()
const debugStore = useDebugDialogStore()

const { isTabFocused } = storeToRefs(layoutStore)

const isOldTypeCard = computed(() => !props.type)
const component = ref<ComponentPublicInstance | null>(null)
const isSquareTypeCard = computed(() => props.type === 'square' || props.type === 'square-without-title')
const cardAppearance = computed(() => (isSquareTypeCard.value ? 'icon' : props.appearance))
const ctrData = computed(() =>
    props.game && props.ctrContext ? { target: props.game.hru, ...props.ctrContext } : undefined,
)
const cardSrc = computed(() => (isSquareTypeCard.value && props.game?.icon) || props.game?.preview)
const gameTitle = computed(() => props.game?.title || '\u00A0' /* non-breaking space to preserve title space */)

const microdataAttrs = computed(() =>
    props.disableMicrodata ? {} : { itemscope: true, itemtype: 'https://schema.org/VideoGame', itemprop: 'url' },
)

const tr = useTr()

const isUnavailableOnPlatform = computed(
    () =>
        !!props.game?.platforms && props.game.platforms.length > 0 && !props.game.platforms.includes(appStore.platform),
)

const availabilityText = computed(() => {
    const names = props.game?.platforms?.map((p) => {
        switch (p) {
            case 'desktop':
                return tr('gameContainer.desktop')
            case 'android':
                return tr('gameContainer.android')
            case 'ios':
                return tr('gameContainer.ios')
            default:
                return p
        }
    })

    return names?.length ? tr('gameCard.availableOn', { platforms: names.join(', ') }) : ''
})

const getExternalUrl = useGetExternalUrl()
const { add, remove } = useCtrStore()

const linkData = computed(() => {
    const to = props.to || (props.game && { name: ROUTE_NAMES.GAME_PAGE, params: { game: props.game.hru } })
    return appStore.isAppEnv ? { to } : { externalTo: to && getExternalUrl(to), target: props.targetLink }
})

const videoElemRef = ref<InstanceType<typeof GameVideo>>()
const isMouseOver = ref(false)
const transformOrigin = ref<string>('center center')

function calculateTransformOrigin() {
    if (!component.value?.$el || typeof window === 'undefined') {
        return 'center center'
    }

    const element = component.value.$el as HTMLElement
    const rect = element.getBoundingClientRect()
    const screenWidth = window.innerWidth
    const leftThreshold = screenWidth * 0.1
    const rightThreshold = screenWidth * 0.9

    if (rect.left < leftThreshold) {
        return 'left center'
    } else if (rect.right > rightThreshold) {
        return 'right center'
    } else {
        return 'center center'
    }
}

function onMouseEnter() {
    if (appStore.platform === 'desktop') {
        videoElemRef.value?.showVideo()
        isMouseOver.value = true

        if (props.withScaleOnHover) {
            transformOrigin.value = calculateTransformOrigin()
        }
    }
}

function onMouseLeave() {
    videoElemRef.value?.closeVideo()
    isMouseOver.value = false
}

function showDebugInfo() {
    debugStore.setDebugData({
        title: props.game?.title || '',
        hru: props.game?.hru || '',
        data: props.game?.debugInfo,
    })
}

watch(isTabFocused, (focused) => {
    if (!focused) {
        onMouseLeave()
    }
})

// nothing reactive is created unless autoplay can actually happen (app + phone + experiment)
if (isHeroVideoAutoplayEnabled()) {
    const isInViewport = ref(false)
    const autoplayTarget = computed(() => (props.autoplayVideoInViewport ? component.value : undefined))

    const videoToPlay = computed(() => {
        if (!props.autoplayVideoInViewport || !isInViewport.value || !isTabFocused.value) {
            return ''
        }
        //we return video url because game in card can be changed, and simple boolean var
        // would not trigger watch in this case
        return props.game?.videoUrl || props.game?.videoId || ''
    })

    useIntersectionObserver(autoplayTarget, ([entry]) => {
        isInViewport.value = !!entry?.isIntersecting
    })

    watch(
        videoToPlay,
        (source, prevSource) => {
            if (source) {
                videoElemRef.value?.showVideo()
            } else if (prevSource) {
                videoElemRef.value?.closeVideo()
            }
        },
        { flush: 'post' },
    )
}

onMounted(() => {
    watch(
        [ctrData, component],
        ([context, cardComponent], _, onCleanup) => {
            const element = cardComponent?.$el as HTMLElement | undefined

            if (!element || !context) {
                return
            }

            add(element, context)
            onCleanup(() => remove(element))
        },
        { immediate: true },
    )
})
</script>

<style module>
.container {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.title {
    display: none;
    position: relative;
}

.new_title {
    color: rgba(255, 255, 255, 0.56);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    max-width: 100%;
    display: block;
}

.container:hover .new_title {
    color: #d7b8ff;
}

.game_card_override:has(video) img {
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    animation-name: video-loading-pulse;
    animation-timing-function: ease-out;
    animation-fill-mode: both;
}

.game_card_override.square_override {
    aspect-ratio: 1;
}

@keyframes video-loading-pulse {
    from {
        transform: scale(1);
    }
    to {
        transform: scale(1.05);
    }
}

.card_with_scale_override {
    transition:
        transform 300ms ease,
        z-index 1s step-end;
}

.card_with_scale_override.hovered_override {
    transform: scale(1.4);
    transition-delay: 150ms;
    z-index: 1;
}

.card_with_scale_override + .badge_override {
    filter: opacity(100%);
    transition: filter 0s linear 300ms;
}

.card_with_scale_override.hovered_override + .badge_override {
    filter: opacity(0);
    transition: filter 0s;
}

.game_card_override:hover .title,
.game_card_override:focus-visible .title {
    display: block;
    grid-area: main;
    padding: 8px 12px 8px;
    align-self: end;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%);
}

.platform_overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    padding: 4px;
}

.platform_overlay_label {
    padding: 10px 8px;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 16px;
    backdrop-filter: blur(2px);
    text-align: center;
}

.debug_icon {
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 16px;
    line-height: 25px;
    text-align: center;
    background: var(--white);
    color: var(--carbon-400);
    transition: all 0.3s ease-in-out;
}

@media (--desktop-small-plus) {
    .debug_icon {
        opacity: 0;
    }

    .game_card_override:hover .debug_icon {
        opacity: 1;
    }

    .debug_icon:hover {
        background: var(--white-80);
        color: var(--carbon-500);
    }
}
</style>
